import { COUNTRY, isDefined, isEmpty, pickFrom, silenceError } from '@nfctron/nexus-core';
import { UiBadge, cn, useIsMounted } from '@nfctron/nexus-react';
import {
	AppStoreCsIcon,
	AppStoreDeIcon,
	AppStoreEnIcon,
	AppStoreSkIcon,
	GooglePlayCsIcon,
	GooglePlayDeIcon,
	GooglePlayEnIcon,
	GooglePlaySkIcon,
} from '@nfctron/nexus-react-svg';

import { Badge, Container, Divider, Text } from '@mantine/core';
import Image from 'next/image';

import packageJson from '@root/config/package.json';

import { Address } from '@root/lib/components/Address/Address';
import { FancyLink } from '@root/lib/components/FancyLink/FancyLink';
import { Trans } from '@root/lib/components/Trans/Trans';
import { APP_I18N_NAMESPACE } from '@root/lib/constants/common';
import { countryEnumTickets } from '@root/lib/constants/country';
import { type CompanyEntity } from '@root/lib/entities/company.entity';
import { type OrganizerColorConfig } from '@root/lib/entities/organizer.entity';
import { useNow } from '@root/lib/hooks/useNow';
import { useOrgIntegratorConfig } from '@root/lib/hooks/useOrgIntegrators';
import { useTranslate } from '@root/lib/hooks/useTranslation';
import { useCookiesConsentContext } from '@root/lib/modules/cookies/CookiesConsentProvider';
import { type AtomComponent } from '@root/lib/types/components';

/**
 * Event/EventFooter atom component
 * @author filipditrich <ditrich@nfctron.com>
 */
export const EventFooterAtom: AtomComponent<EventFooterAtomProps> = ({
	eventName,
	organizerName,
	organizerColors,
	organizerLogoUrl,
	ticketCompany,
	productCompany,
}) => {
	const { t, lang } = useTranslate(APP_I18N_NAMESPACE.MAIN, EventFooterAtom.displayName);
	const orgIntegratorConfig = useOrgIntegratorConfig();
	const cookieConsentCtx = useCookiesConsentContext();
	const isProductCompanySlovakia = productCompany.country.isoChar3 === countryEnumTickets.get(COUNTRY.SVK).isoChar3;
	const isTicketCompanySlovakia = ticketCompany.country.isoChar3 === countryEnumTickets.get(COUNTRY.SVK).isoChar3;
	const now = useNow();
	const isMounted = useIsMounted();
	const supportLink = silenceError(() => {
		const base = new URL('mailto:info@nfctron.com');
		if (!isMounted) return base;
		base.searchParams.set('subject', t('supportEmail.subject', { eventName }));
		base.searchParams.set('body', t('supportEmail.body', { eventName, eventUrl: window.location.href }));
		return base;
	}, new URL('mailto:info@nfctron.com'));

	const AppStoreLogo = {
		cs: AppStoreCsIcon,
		en: AppStoreEnIcon,
		de: AppStoreDeIcon,
		sk: AppStoreSkIcon,
	}[lang];
	const GooglePlayLogo = {
		cs: GooglePlayCsIcon,
		en: GooglePlayEnIcon,
		de: GooglePlayDeIcon,
		sk: GooglePlaySkIcon,
	}[lang];

	return (
		<footer
			className="bg-lucky-point-600 relative overflow-x-clip p-4 text-white transition-all lg:p-6"
			style={{
				backgroundColor: organizerColors.background,
				color: organizerColors.text,
			}}
		>
			{/* snow caps */}
			{/*<img src="/static/images/snow_cap_1.svg" height={100} className="absolute left-[-10px] top-[-25px]" alt="" />*/}
			{/*<img src="/static/images/snow_cap_2.svg" height={100} className="absolute right-[-10px] top-[-25px]" alt="" />*/}

			<Container size="responsive" className="my-auto flex max-w-full flex-col items-center gap-4 p-0 text-sm md:gap-6 xl:max-w-screen-xl xl:px-6">
				{/* organizer footer section */}
				<section className={cn('flex w-full items-start gap-4')} style={{ color: organizerColors.text }}>
					<div className="flex grow flex-row gap-4 md:flex-wrap">
						{/* ticket company */}
						<div>
							{/* ticket company badge (if different from product) */}
							{ticketCompany.regNumber !== productCompany.regNumber && (
								<UiBadge
									variant="unstyled"
									size="unstyled"
									className="mb-0.5 rounded-full border border-zinc-500 px-2 text-xs font-bold text-zinc-500 uppercase"
								>
									{t('ticketCompanyBadge.text')}
								</UiBadge>
							)}
							{/* company name */}
							<Text fw={700} className="text-base md:text-lg">
								{ticketCompany.legalName}
							</Text>
							{/* legal info */}
							<div className="opacity-75">
								{/* regNumber */}
								{!isEmpty(ticketCompany.regNumber) && (
									<Text className="text-sm md:text-base">
										{t('organizerRegNumber.text', {
											regNumber: ticketCompany.regNumber,
											context: ticketCompany.country.isoChar3,
										})}
									</Text>
								)}
								{/* slovakia taxId */}
								{!isEmpty(ticketCompany.taxId) && isTicketCompanySlovakia && (
									<Text className="text-sm md:text-base">
										{t('organizerTaxId.text', {
											taxId: ticketCompany.taxId,
											context: ticketCompany.country.isoChar3,
										})}
									</Text>
								)}
								{/* vatId */}
								{!isEmpty(ticketCompany.vatId) && (
									<Text className="text-sm md:text-base">
										{t('organizerVatId.text', {
											vatId: ticketCompany.vatId,
											context: ticketCompany.country.isoChar3,
										})}
									</Text>
								)}
								{/* taxId */}
								{!isEmpty(ticketCompany.taxId) && !isTicketCompanySlovakia && (
									<Text className="text-sm md:text-base">
										{t('organizerTaxId.text', {
											taxId: ticketCompany.taxId,
											context: ticketCompany.country.isoChar3,
										})}
									</Text>
								)}
							</div>
							{/* address */}
							<div className="mt-3 opacity-75">
								<Address
									className="text-sm not-italic md:text-base"
									address={{
										...pickFrom(ticketCompany, 'address1', 'address2', 'city', 'postalCode'),
										countryId: ticketCompany.country.countryId,
									}}
								/>
							</div>
						</div>

						{/* product company (if different from ticket) */}
						{ticketCompany.regNumber !== productCompany.regNumber && (
							<div>
								<UiBadge
									variant="unstyled"
									size="unstyled"
									className="mb-0.5 rounded-full border border-zinc-500 px-2 text-xs font-bold text-zinc-500 uppercase"
								>
									{t('productCompanyBadge.text')}
								</UiBadge>
								{/* company name */}
								<Text fw={700} className="text-base md:text-lg">
									{productCompany.legalName}
								</Text>
								{/* legal info */}
								<div className="opacity-75">
									{/* regNumber */}
									{!isEmpty(productCompany.regNumber) && (
										<Text className="text-sm md:text-base">
											{t('organizerRegNumber.text', {
												regNumber: productCompany.regNumber,
												context: productCompany.country.isoChar3,
											})}
										</Text>
									)}
									{/* slovakia taxId */}
									{!isEmpty(productCompany.taxId) && isProductCompanySlovakia && (
										<Text className="text-sm md:text-base">
											{t('organizerTaxId.text', {
												taxId: productCompany.taxId,
												context: productCompany.country.isoChar3,
											})}
										</Text>
									)}
									{/* vatId */}
									{!isEmpty(productCompany.vatId) && (
										<Text className="text-sm md:text-base">
											{t('organizerVatId.text', {
												vatId: productCompany.vatId,
												context: productCompany.country.isoChar3,
											})}
										</Text>
									)}
									{/* taxId */}
									{!isEmpty(productCompany.taxId) && !isProductCompanySlovakia && (
										<Text className="text-sm md:text-base">
											{t('organizerTaxId.text', {
												taxId: productCompany.taxId,
												context: productCompany.country.isoChar3,
											})}
										</Text>
									)}
								</div>
								{/* address */}
								<div className="mt-3 opacity-75">
									<Address
										className="text-sm not-italic md:text-base"
										address={{
											...pickFrom(productCompany, 'address1', 'address2', 'city', 'postalCode'),
											countryId: productCompany.country.countryId,
										}}
									/>
								</div>
							</div>
						)}
					</div>

					{/* right column */}
					{!isEmpty(organizerLogoUrl) && (
						<div className="relative ml-auto flex max-h-[100px] max-w-1/3">
							<Image className="object-contain object-right-top" src={organizerLogoUrl} alt={organizerName} loading="lazy" width={100} height={100} />
						</div>
					)}
				</section>

				{/* divider */}
				<Divider style={{ borderColor: organizerColors.text }} className="w-full px-12 opacity-10" />

				{/* nfctron footer section */}
				<section className={cn('mb-md flex w-full flex-col gap-2 sm:gap-3')} style={{ color: organizerColors.text }}>
					{/* copyright */}
					<Text className="text-base sm:text-lg" fw={600}>
						<Trans tFunc={t} i18nKey="copyright.text" values={{ year: now.getFullYear() }} />
					</Text>

					<div className={cn('flex flex-col gap-1 md:gap-4 xl:flex-row xl:items-center')}>
						{/* legal link */}
						<Text className="text-base md:text-lg">
							<FancyLink
								href={
									orgIntegratorConfig?.termsUrl?.href ??
									t('legalLink.href', {
										context: ticketCompany.country.isoChar3,
									})
								}
								target="_blank"
								rel="noreferrer"
								className="underline opacity-75 transition hover:opacity-100"
							>
								{t('legalLink.text', {
									context: ticketCompany.country.isoChar3,
								})}
							</FancyLink>
						</Text>

						{/* support link */}
						<Text className="text-base md:text-lg">
							<FancyLink href={supportLink.href.replace(/\+/g, ' ')} rel="noreferrer" className="underline opacity-75 transition hover:opacity-100">
								{t('supportEmail.text')}
							</FancyLink>
						</Text>

						{/* cookies settings */}
						<Text className="text-base md:text-lg" onClick={cookieConsentCtx.openCookiesSettingsModal}>
							<FancyLink
								href="#"
								onClick={(e) => {
									e.preventDefault();
								}}
								className="underline opacity-75 transition hover:opacity-100"
							>
								{t('cookiesSettings.text')}
							</FancyLink>
						</Text>

						{/* mobile app stores */}
						<div className="mt-2 flex items-center gap-3 md:mt-0 lg:ml-auto">
							{/* app store */}
							{isDefined(AppStoreLogo) && (
								<a
									href="https://apps.apple.com/cz/app/nfctron/id1595341751"
									target="_blank"
									rel="noreferrer"
									className="tranition-opacity opacity-75 hover:opacity-100"
								>
									<AppStoreLogo className="h-8 w-[112px] text-white" style={{ color: organizerColors.text }} />
								</a>
							)}
							{/* google play */}
							{isDefined(GooglePlayLogo) && (
								<a
									href="https://play.google.com/store/apps/details?id=com.nfctron.mobile"
									target="_blank"
									rel="noreferrer"
									className="tranition-opacity opacity-75 hover:opacity-100"
								>
									<GooglePlayLogo className="h-8 w-[112px] text-white" style={{ color: organizerColors.text }} />
								</a>
							)}
						</div>
					</div>
					{/* version */}
					<Text className="mt-2 text-sm opacity-50">
						{t('version.text', {
							version: packageJson.version,
						})}
					</Text>
				</section>
			</Container>
		</footer>
	);
};

/**
 * Event/EventFooter atom props
 * @export
 */
export type EventFooterAtomProps = {
	organizerName: string;
	organizerColors: OrganizerColorConfig;
	organizerLogoUrl?: string;
	eventName: string;
	ticketCompany: CompanyEntity;
	productCompany: CompanyEntity;
};

/**
 * Event/EventFooter atom display name
 * @type {ComponentDisplayName<"atom">>}
 */
EventFooterAtom.displayName = 'Event/EventFooterAtom';
